import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
export class PlatformAccountPasswordFailure extends Error {
    code;
    name = 'PlatformAccountPasswordFailure';
    constructor(code) {
        super('The password change could not be completed.');
        this.code = code;
    }
}
export function platformAccountPasswordFailureMessage(error) {
    if (!(error instanceof PlatformAccountPasswordFailure))
        return 'The password could not be changed. Try again when your connection is available.';
    switch (error.code) {
        case 'current-password-required': return 'Enter your current password.';
        case 'new-password-length': return 'Use 8 to 128 characters for your new password.';
        case 'password-confirmation': return 'The new passwords do not match.';
        case 'current-password-rejected': return 'Your current password could not be verified. Check it and try again.';
        case 'password-policy': return 'The new password was not accepted. Choose a different password that meets your account requirements.';
        case 'reauthentication-required': return 'Additional account verification is required. Sign in again or contact your administrator.';
        case 'session-changed': return 'Your account context changed. Reopen Account before trying again.';
        case 'unavailable': return 'The password could not be changed. Try again when your connection is available.';
    }
}
export function readPlatformAccountPasswordInput(values) {
    const currentPassword = values.get('current-password');
    const newPassword = values.get('new-password');
    const confirmation = values.get('password-confirmation');
    if (typeof currentPassword !== 'string' || currentPassword.length === 0) {
        throw new PlatformAccountPasswordFailure('current-password-required');
    }
    if (typeof newPassword !== 'string' || newPassword.length < 8 || newPassword.length > 128) {
        throw new PlatformAccountPasswordFailure('new-password-length');
    }
    if (newPassword !== confirmation)
        throw new PlatformAccountPasswordFailure('password-confirmation');
    // Passwords are deliberately not trimmed or normalized.
    return { currentPassword, newPassword };
}
/** Used by the form itself: invalidation blocks later effects, not a PUT already sent. */
export class PlatformAccountPasswordSubmission {
    active = false;
    generation = 0;
    pending = false;
    get isPending() { return this.pending; }
    activate() {
        this.active = true;
        this.generation += 1;
        this.pending = false;
    }
    deactivate() {
        this.active = false;
        this.generation += 1;
        this.pending = false;
    }
    reset() {
        this.generation += 1;
        this.pending = false;
    }
    async run(input, action) {
        if (!this.active)
            return { status: 'superseded' };
        if (this.pending)
            return { status: 'busy' };
        const generation = this.generation;
        const isCurrent = () => this.active && generation === this.generation;
        this.pending = true;
        try {
            await action(input, isCurrent);
            return { status: isCurrent() ? 'changed' : 'superseded' };
        }
        catch (error) {
            return isCurrent() ? { status: 'failed', error } : { status: 'superseded' };
        }
        finally {
            if (isCurrent())
                this.pending = false;
        }
    }
}
export function PlatformAccountPasswordPanel(props) {
    const submission = useMemo(() => new PlatformAccountPasswordSubmission(), [props.scopeKey]);
    const form = useRef(null);
    const [pending, setPending] = useState(false);
    const [message, setMessage] = useState(null);
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        submission.activate();
        form.current?.reset();
        setPending(false);
        setMessage(null);
        setFailed(false);
        return () => {
            submission.deactivate();
            form.current?.reset();
        };
    }, [submission]);
    const submit = async (event) => {
        event.preventDefault();
        if (submission.isPending)
            return;
        const element = event.currentTarget;
        let input;
        try {
            input = readPlatformAccountPasswordInput(new FormData(element));
        }
        catch (error) {
            element.reset();
            setFailed(true);
            setMessage(platformAccountPasswordFailureMessage(error));
            return;
        }
        element.reset();
        setPending(true);
        setMessage(null);
        setFailed(false);
        const result = await submission.run(input, props.changePassword);
        if (result.status === 'superseded' || result.status === 'busy')
            return;
        setPending(false);
        if (result.status === 'failed') {
            setFailed(true);
            setMessage(platformAccountPasswordFailureMessage(result.error));
        }
        else {
            setMessage('Password changed. Sign in with your new password.');
        }
    };
    return (_jsxs("section", { className: "crest-card crest-card-wide", "aria-labelledby": "crest-account-password-title", children: [_jsxs("header", { className: "crest-page-heading", children: [_jsx("h1", { id: "crest-account-password-title", children: "Account" }), _jsx("p", { children: "Change the password for your signed-in account." })] }), _jsxs("form", { ref: form, className: "crest-settings-editor", onSubmit: (event) => void submit(event), "aria-busy": pending, "aria-describedby": "crest-account-password-help", children: [_jsx("h2", { children: "Change password" }), message === null ? null : (_jsx("p", { role: failed ? 'alert' : 'status', "aria-live": "polite", children: message })), _jsxs("label", { className: "crest-field", htmlFor: "crest-account-current-password", children: [_jsx("span", { children: "Current password" }), _jsx("input", { className: "crest-input", id: "crest-account-current-password", name: "current-password", type: "password", autoComplete: "current-password", required: true, disabled: pending })] }), _jsxs("label", { className: "crest-field", htmlFor: "crest-account-new-password", children: [_jsx("span", { children: "New password" }), _jsx("input", { className: "crest-input", id: "crest-account-new-password", name: "new-password", type: "password", autoComplete: "new-password", minLength: 8, maxLength: 128, required: true, disabled: pending })] }), _jsxs("label", { className: "crest-field", htmlFor: "crest-account-password-confirmation", children: [_jsx("span", { children: "Confirm new password" }), _jsx("input", { className: "crest-input", id: "crest-account-password-confirmation", name: "password-confirmation", type: "password", autoComplete: "new-password", minLength: 8, maxLength: 128, required: true, disabled: pending })] }), _jsx("p", { id: "crest-account-password-help", children: "Use 8 to 128 characters. Your current password is required. After changing it, sign in again." }), _jsxs("div", { className: "crest-button-row", children: [_jsx("button", { className: "crest-button-primary", type: "submit", disabled: pending, children: pending ? 'Changing password…' : 'Change password' }), _jsx("button", { className: "crest-button-secondary", type: "button", disabled: pending, onClick: () => {
                                    submission.reset();
                                    form.current?.reset();
                                    setMessage(null);
                                    setFailed(false);
                                }, children: "Cancel" })] })] })] }));
}
//# sourceMappingURL=platform-account-password.panel.js.map